import "./CustomDatePicker.css";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { useAccessRights } from "../../../hooks/userPermissions/useAccessRights";
import {
  getForecastLimitDateOnly,
  getMaxHourForDate,
} from "../../../utils/weatherTimelineLimits";

const CustomDatePicker = ({
  onChange,
  weatherRequestDate,
  weatherRequestStartHour,
  onHourChange,
  disabled,
}) => {
  const { getTimelineLimits } = useAccessRights();
  const timelineLimits = getTimelineLimits("weather");
  const intervalMinJ = timelineLimits?.intervalMinJ ?? -5;
  const intervalMaxJ = timelineLimits?.intervalMaxJ ?? null;

  const toDateOnly = (date) =>
    new Date(date.getFullYear(), date.getMonth(), date.getDate());

  const nowInFrance = new Date(
    new Date().toLocaleString("en-US", { timeZone: "Europe/Paris" }),
  );
  const today = toDateOnly(nowInFrance);

  const minDate = new Date(today);
  minDate.setDate(today.getDate() + intervalMinJ);

  // the user's forecast window genuinely reaches into future days.
  const maxDate = getForecastLimitDateOnly(intervalMaxJ) ?? today;

  const currentDate = weatherRequestDate
    ? toDateOnly(
        new Date(
          `${weatherRequestDate.year}-${weatherRequestDate.month}-${weatherRequestDate.day}`,
        ),
      )
    : today;

  const isAtMinDate = currentDate.getTime() === minDate.getTime();
  const isAtMaxDate = currentDate.getTime() === maxDate.getTime();

  const changeDate = (newDate) => {
    let clampedDate = new Date(newDate);
    if (clampedDate < minDate) clampedDate = new Date(minDate);
    if (clampedDate > maxDate) clampedDate = new Date(maxDate);

    const formattedDate = {
      day: clampedDate.getDate().toString().padStart(2, "0"),
      month: (clampedDate.getMonth() + 1).toString().padStart(2, "0"),
      year: clampedDate.getFullYear().toString(),
    };

    if (onChange) {
      onChange(formattedDate);
    }

    if (onHourChange && weatherRequestStartHour !== undefined) {
      const maxHour = getMaxHourForDate(formattedDate, intervalMaxJ);
      if (maxHour >= 0 && weatherRequestStartHour > maxHour) {
        onHourChange(maxHour);
      }
    }
  };

  const incrementDay = () => {
    if (isAtMaxDate) return;
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() + 1);
    changeDate(newDate);
  };

  const decrementDay = () => {
    if (isAtMinDate) return;
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() - 1);
    changeDate(newDate);
  };

  const handleDayChange = (e) => {
    const value = e.target.value.replace(/\D/g, "") || "1";
    const day = Math.min(31, Math.max(1, parseInt(value)))
      .toString()
      .padStart(2, "0");

    const newDate = new Date(currentDate);
    newDate.setDate(parseInt(day));
    changeDate(newDate);
  };

  const handleMonthChange = (e) => {
    const value = e.target.value.replace(/\D/g, "") || "1";
    const month = Math.min(12, Math.max(1, parseInt(value)))
      .toString()
      .padStart(2, "0");

    const newDate = new Date(currentDate);
    newDate.setMonth(parseInt(month) - 1);
    changeDate(newDate);
  };

  const handleYearChange = (e) => {
    const value = e.target.value.replace(/\D/g, "").substring(0, 4) || "2023";

    const newDate = new Date(currentDate);
    newDate.setFullYear(parseInt(value));
    changeDate(newDate);
  };

  return (
    <div className="date-picker-fr" data-testid="date-picker-fr">
      <div className="date-component">
        <KeyboardArrowUpIcon
          className={`arrow ${isAtMaxDate || disabled ? "arrow-disabled" : ""}`}
          onClick={disabled ? undefined : incrementDay}
          disabled={isAtMaxDate || disabled}
          style={{ fontSize: "19px" }}
          data-testid="date-picker-day-increment"
        />
        <input
          type="text"
          value={weatherRequestDate?.day || ""}
          onChange={disabled ? undefined : handleDayChange}
          maxLength={2}
          className={`day-input ${disabled ? "weather-input-disabled" : ""}`}
          disabled={disabled}
          readOnly
          data-testid="date-picker-day-input"
        />
        <KeyboardArrowDownIcon
          className={`arrow ${isAtMinDate || disabled ? "arrow-disabled" : ""}`}
          onClick={disabled ? undefined : decrementDay}
          disabled={isAtMinDate || disabled}
          style={{ fontSize: "19px" }}
          data-testid="date-picker-day-decrement"
        />
      </div>

      <div className="date-component">
        <KeyboardArrowUpIcon disabled className="arrow arrow-disabled" style={{ fontSize: "19px" }} />
        <input
          type="text"
          data-testid="date-picker-month-input"
          value={weatherRequestDate?.month || ""}
          onChange={handleMonthChange}
          maxLength={2}
          className="month-input weather-input-disabled"
        />
        <KeyboardArrowDownIcon className="arrow arrow-disabled" disabled style={{ fontSize: "19px" }} />
      </div>

      <div className="date-component">
        <KeyboardArrowUpIcon disabled className="arrow arrow-disabled" style={{ fontSize: "19px" }} />
        <input
          type="text"
          value={weatherRequestDate?.year || ""}
          onChange={handleYearChange}
          maxLength={4}
          className="year-input weather-input-disabled"
          data-testid="date-picker-year-input"
        />
        <KeyboardArrowDownIcon className="arrow arrow-disabled" style={{ fontSize: "19px" }} disabled />
      </div>
    </div>
  );
};

export default CustomDatePicker;